import React, { useState, useRef, useEffect, use } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, VideoCameraIcon, CalendarIcon, UserIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button } from "components/ui";
import { Input, Textarea, Switch, Spinner, Select } from "components/ui";
import { createGoogleMeetLink, updateMeetLink } from "services/googleMeetService";
import { getUsersForTeamType } from "services/teamsService";
import { getLocalDateTime, toUTCISOString } from "../../../../utils/dateUtils";
import { toast } from "sonner";
import clsx from "clsx";

const MeetModal = ({
  isOpen,
  onClose,
  leadData,
  meeting,
  selectedDate,
  onMeetingSaved,
}) => {
  const isEditMode = !!meeting;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startTime: "",
    endTime: "",
    leadId: "",
    participants: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const saveRef = useRef(null);
  const errorRef = useRef(null);



  // Fetch users for team type COUN when modal opens
  useEffect(() => {
    const fetchCounselorUsers = async () => {
      if (!isOpen) {
        setUsers([]);
        return;
      }

      setLoadingUsers(true);
      const result = await getUsersForTeamType("COUN");

      if (result.success) {
        // Normalize UserId to lowercase so they match stored participants
        const normalized = (result.data || []).map(u => ({
          ...u,
          UserId: u.UserId?.toLowerCase() || u.UserId
        }));
        setUsers(normalized);
      } else {
        setUsers([]);
        console.error("Failed to fetch counselor users:", result.message);
      }

      setLoadingUsers(false);
    };

    fetchCounselorUsers();
  }, [isOpen]);

  // Reset form when modal opens/closes or when meeting data changes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && meeting) {
        // Edit mode - populate with existing meeting data
        setFormData({
          title: meeting.title || "",
          description: meeting.description || "",
          startTime: formatDateTimeLocal(getLocalDateTime(meeting.startTime)),
          endTime: formatDateTimeLocal(getLocalDateTime(meeting.endTime)),
          leadId: leadData?.id,
          // Normalize participants to lowercase
          participants: (meeting.participants || []).map(id => id?.toLowerCase())
        });
      } else {
        // Create mode - use selected date or default to current time
        const startTime = selectedDate ? new Date(selectedDate) : new Date();
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // 1 hour duration

        setFormData({
          title: ``,
          description: "",
          startTime: formatDateTimeLocal(startTime),
          endTime: formatDateTimeLocal(endTime),
          leadId: leadData?.id,
          participants: leadData?.id ? [leadData?.id.toLowerCase()] : []
        });
      }
      setError(null);
      setIsSubmitting(false);
      setSelectedUserId("");
    }
  }, [isOpen, leadData, meeting, isEditMode, selectedDate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) {
      setError(null);
    }
  };

  const handleSelectUserToAdd = (userId) => {
    if (!userId) return;

    const normalizedId = userId.toLowerCase();

    if (formData.participants.includes(normalizedId)) {
      toast.warning("This member is already added");
      setSelectedUserId("");
      return;
    }

    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, normalizedId]
    }));

    setSelectedUserId("");
  };

  const handleRemoveMember = (userId) => {
    const normalizedId = userId.toLowerCase();

    if (normalizedId === leadData?.id?.toLowerCase()) {
      toast.warning("Cannot remove the candidate from participants");
      return;
    }

    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter(id => id !== normalizedId)
    }));
  };

  const getUserName = (userId) => {
    const normalizedId = userId?.toLowerCase();

    // lead might be stored in any case; compare in lowercase
    if (normalizedId === leadData?.id?.toLowerCase()) {
      return leadData?.name || "Candidate";
    }

    // Find in counselor users by lowercase UserId
    const user = users.find(u => u.UserId?.toLowerCase() === normalizedId);
    return user ? user.FullName : "Unknown User";
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError("Meeting title is required");
      scrollToError();
      return false;
    }
    if (!formData.description.trim()) {
      setError("Meeting description is required");
      scrollToError();
      return false;
    }

    // Enhanced start time validation
    if (!formData.startTime || formData.startTime.trim() === "") {
      setError("Start time is required");
      scrollToError();
      return false;
    }

    // Check if start time is a valid date
    const startDateTime = new Date(formData.startTime);
    if (isNaN(startDateTime.getTime())) {
      setError("Please enter a valid start time");
      scrollToError();
      return false;
    }

    // Enhanced end time validation
    if (!formData.endTime || formData.endTime.trim() === "") {
      setError("End time is required");
      scrollToError();
      return false;
    }

    // Check if end time is a valid date
    const endDateTime = new Date(formData.endTime);
    if (isNaN(endDateTime.getTime())) {
      setError("Please enter a valid end time");
      scrollToError();
      return false;
    }

    if (startDateTime >= endDateTime) {
      setError("End time must be after start time");
      scrollToError();
      return false;
    }

    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    if (startDateTime < fiveMinutesAgo) {
      setError("Start time cannot be more than 5 minutes in the past");
      scrollToError();
      return false;
    }

    return true;
  };

  // Function to scroll to error message
  const scrollToError = () => {
    setTimeout(() => {
      if (errorRef.current) {
        errorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100); // Small delay to ensure error is rendered
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        startTime: toUTCISOString(new Date(formData.startTime)),
        endTime: toUTCISOString(new Date(formData.endTime)),
        leadId: formData.leadId,
        participants: formData.participants
      };

      let result;
      if (isEditMode) {
        result = await updateMeetLink({
          ...payload,
          meetingId: meeting.id,
          eventId: meeting.googleEventId
        });
      } else {
        result = await createGoogleMeetLink(payload);
      }

      if (result.success) {
        toast.success(`Google Meet ${isEditMode ? 'updated' : 'created'} successfully!`);
        onMeetingSaved?.(result.data);
        onClose();
      } else {
        setError(result.error || `Failed to ${isEditMode ? 'update' : 'create'} Google Meet link`);
        toast.error(result.error || `Failed to ${isEditMode ? 'update' : 'create'} Google Meet link`);
        scrollToError(); // Scroll to error when API call fails
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} Google Meet link:`, error);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} Google Meet link. Please try again.`);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} Google Meet link`);
      scrollToError(); // Scroll to error when exception occurs
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTimeLocal = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Filter participants to exclude leadId for display purposes only
  const displayParticipants = formData.participants.filter(
    userId => userId?.toLowerCase() !== leadData?.id?.toLowerCase()
  );

  // Get available users (excluding already selected ones)
  const availableUsers = users.filter(
    user => !formData.participants.includes(user.UserId?.toLowerCase())
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
        static
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-lg origin-top flex-col rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none h-[85vh]">
            {/* Header - Fixed */}
            <div className="dark:bg-dark-800 flex shrink-0 items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
              <DialogTitle
                as="h3"
                className="dark:text-dark-100 flex items-center gap-2 text-base font-medium text-gray-800"
              >
                <VideoCameraIcon className="h-5 w-5 text-blue-600" />
                {isEditMode ? "Edit Google Meet" : "Schedule Google Meet"}
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Body (scrollable) */}
            <div className="flex flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-4 py-4 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 dark:scrollbar-thumb-dark-500 dark:scrollbar-track-dark-700 sm:px-5">
                {error && (
                  <div ref={errorRef} className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Lead Info */}
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <UserIcon className="h-4 w-4" />
                      <span>Candidate: {leadData?.name || "N/A"}</span>
                    </div>
                  </div>

                  {/* Meeting Title */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Meeting Title <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange("title", e.target.value)}
                      placeholder="Enter meeting title"
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange("description", e.target.value)}
                      placeholder="Enter meeting description"
                      rows={3}
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  {/* Date and Time */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="datetime-local"
                        value={formData.startTime}
                        onChange={(e) => handleInputChange("startTime", e.target.value)}
                        disabled={isSubmitting}
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="datetime-local"
                        value={formData.endTime}
                        onChange={(e) => handleInputChange("endTime", e.target.value)}
                        disabled={isSubmitting}
                        required
                      />
                    </div>
                  </div>

                  {/* Duration Display */}
                  {formData.startTime && formData.endTime && (
                    <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                      <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                        <CalendarIcon className="h-4 w-4" />
                        <span>
                          Duration: {Math.round((new Date(formData.endTime) - new Date(formData.startTime)) / (1000 * 60))} minutes
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Team Members Section */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-900 dark:text-dark-100">
                      Meeting Participants ({displayParticipants.length})
                    </label>

                    {/* Current Members as Chips */}
                    {displayParticipants.length > 0 ? (
                      <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-300 bg-gray-50 p-3 dark:border-dark-500 dark:bg-dark-800 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 dark:scrollbar-thumb-dark-500 dark:scrollbar-track-dark-700">
                        <div className="flex flex-wrap gap-2">
                          {displayParticipants.map((userId) => (
                            <div
                              key={userId}
                              className="inline-flex items-center gap-1.5 rounded-md bg-primary-100 px-3 py-1.5 text-sm text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                            >
                              <span className="font-medium">{getUserName(userId)}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveMember(userId)}
                                className="inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-primary-200 dark:hover:bg-primary-800/50"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-gray-300 bg-gray-50 p-4 text-center dark:border-dark-500 dark:bg-dark-800">
                        <p className="text-sm text-gray-500 dark:text-dark-400">
                          No participants added yet
                        </p>
                      </div>
                    )}

                    {/* Add Members Dropdown */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700 dark:text-dark-300">
                        Add Counselors to Meeting
                      </label>

                      {loadingUsers ? (
                        <div className="flex items-center justify-center rounded-lg border border-gray-300 bg-gray-50 py-8 dark:border-dark-500 dark:bg-dark-800">
                          <div className="flex items-center gap-2">
                            <Spinner size="sm" />
                            <span className="text-sm text-gray-500 dark:text-dark-400">
                              Loading counselors...
                            </span>
                          </div>
                        </div>
                      ) : availableUsers.length > 0 ? (
                        <Select
                          value={selectedUserId}
                          onChange={(e) => handleSelectUserToAdd(e.target.value)}
                          disabled={isSubmitting}
                          className="w-full"
                        >
                          <option value="">Select a counselor to add</option>
                          {availableUsers.map((user) => (
                            <option key={user.UserId} value={user.UserId}>
                              {user.FullName} ({user.UserName})
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <div className="rounded-lg border border-gray-300 bg-gray-50 p-3 text-center dark:border-dark-500 dark:bg-dark-800">
                          <p className="text-xs text-gray-500 dark:text-dark-400">
                            {users.length === 0
                              ? "No counselors available"
                              : "All counselors have been added"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="dark:bg-dark-700 shrink-0 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
              <Button
                onClick={onClose}
                variant="outlined"
                className="min-w-[7rem] rounded-full"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                ref={saveRef}
                className="min-w-[7rem] rounded-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Meeting" : "Create Meeting")}
              </Button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default MeetModal;
